
import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { 
  Eye, 
  EyeOff, 
  Calendar, 
  Users, 
  Globe,
  AlertTriangle,
  CheckCircle,
  Star,
  TrendingUp,
  XCircle
} from "lucide-react";

interface EnhancedPublishingSectionProps {
  applicationId: string;
  isApproved: boolean;
  expertStatus: 'active' | 'inactive' | 'draft' | 'pending_approval' | 'suspended' | null;
  loading: boolean;
  onToggleStatus: () => void;
}

export default function EnhancedPublishingSection({
  applicationId,
  isApproved,
  expertStatus,
  loading,
  onToggleStatus
}: EnhancedPublishingSectionProps) {
  const [publishingNotes, setPublishingNotes] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);

  if (!isApproved) {
    return (
      <Card className="border-amber-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-amber-800">
            <AlertTriangle className="w-5 h-5" />
            Publishing Unavailable
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="p-4 bg-amber-50 rounded-lg">
            <p className="text-sm text-amber-800">
              This application must be approved before it can be published to the marketplace.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const isPublished = expertStatus === 'active';
  const isInactive = expertStatus === 'inactive';

  return (
    <div className="space-y-4">
      {/* Main Publishing Card */}
      <Card className={`border-2 ${
        isPublished ? 'border-green-200' : 
        isInactive ? 'border-orange-200' : 'border-blue-200'
      }`}>
        <CardHeader>
          <CardTitle className={`flex items-center gap-2 ${
            isPublished ? 'text-green-800' : 
            isInactive ? 'text-orange-800' : 'text-blue-800'
          }`}>
            {isPublished ? <Globe className="w-5 h-5" /> : 
             isInactive ? <EyeOff className="w-5 h-5" /> :
             <Eye className="w-5 h-5" />}
            Marketplace Publishing
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Status Overview */}
          <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
            <div className="space-y-1">
              <Label className="text-sm font-medium">Current Status</Label>
              <div className="flex items-center gap-2">
                <Badge variant={
                  isPublished ? 'default' : 
                  isInactive ? 'secondary' : 'outline'
                } className={`text-sm ${
                  isPublished ? 'bg-green-600' :
                  isInactive ? 'bg-orange-500' : ''
                }`}>
                  {isPublished ? 'Published' : 
                   isInactive ? 'Unpublished' : 'Not Created'}
                </Badge>
                {isPublished && (
                  <Badge variant="outline" className="text-green-600 border-green-200">
                    <Users className="w-3 h-3 mr-1" />
                    Live on site
                  </Badge>
                )}
              </div>
            </div>
            <Switch
              checked={isPublished}
              onCheckedChange={onToggleStatus}
              disabled={loading}
              className="data-[state=checked]:bg-green-600"
            />
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Button
              variant={isPublished ? "outline" : "default"}
              onClick={onToggleStatus}
              disabled={loading}
              className={`${!isPublished ? 'bg-green-600 hover:bg-green-700' : ''}`}
            >
              {isPublished ? (
                <>
                  <EyeOff className="h-4 w-4 mr-2" />
                  Unpublish Expert
                </>
              ) : (
                <>
                  <Eye className="h-4 w-4 mr-2" />
                  Publish Expert
                </>
              )}
            </Button>
            
            {isPublished && (
              <Button
                variant="outline"
                onClick={() => window.open('/consultations', '_blank')}
                className="border-green-200 text-green-700 hover:bg-green-50"
              >
                <Globe className="h-4 w-4 mr-2" />
                View Live
              </Button>
            )}
          </div>

          {/* Status Information */}
          {isPublished ? (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                <div className="space-y-2">
                  <p className="font-medium text-green-800">Expert Profile is Live</p>
                  <p className="text-sm text-green-700">
                    This expert profile is visible on the consultations marketplace and can receive bookings from clients.
                  </p>
                  <div className="flex items-center gap-4 text-xs text-green-600">
                    <span className="flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      Visible to clients
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      Can receive bookings
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ) : isInactive ? (
            <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
              <div className="flex items-start gap-3">
                <EyeOff className="w-5 h-5 text-orange-600 mt-0.5" />
                <div>
                  <p className="font-medium text-orange-800">Expert Profile Unpublished</p>
                  <p className="text-sm text-orange-700">
                    This expert profile exists but is not visible to clients and cannot receive bookings.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-3">
                <Star className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <p className="font-medium text-blue-800">Ready to Publish</p>
                  <p className="text-sm text-blue-700">
                    This application has been approved and is ready to be published to the marketplace.
                    <br />
                    <span className="text-orange-600 font-medium">
                      Note: A booking link must be configured before publishing.
                    </span>
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Advanced Settings Toggle */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="text-muted-foreground"
          >
            {showAdvanced ? 'Hide' : 'Show'} Advanced Settings
          </Button>

          {/* Advanced Publishing Settings */}
          {showAdvanced && (
            <div className="space-y-4 p-4 border rounded-lg bg-muted/30">
              <div>
                <Label className="text-sm font-medium">Publishing Notes</Label>
                <Textarea
                  placeholder="Add internal notes about this expert's publishing status..."
                  value={publishingNotes}
                  onChange={(e) => setPublishingNotes(e.target.value)}
                  className="mt-2"
                  rows={3}
                />
              </div>
              
              <div className="flex items-center justify-between p-3 border rounded">
                <div>
                  <Label className="text-sm font-medium">Featured Expert</Label>
                  <p className="text-xs text-muted-foreground">Promote this expert on the homepage</p>
                </div>
                <Switch disabled />
              </div>
              
              <div className="flex items-center justify-between p-3 border rounded">
                <div>
                  <Label className="text-sm font-medium">Priority Listing</Label>
                  <p className="text-xs text-muted-foreground">Show this expert at the top of search results</p>
                </div>
                <Switch disabled />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Publishing Analytics */}
      {isPublished && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Publishing Analytics
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-blue-600">0</div>
                <div className="text-xs text-muted-foreground">Profile Views</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-green-600">0</div>
                <div className="text-xs text-muted-foreground">Bookings</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-purple-600">0</div>
                <div className="text-xs text-muted-foreground">Inquiries</div>
              </div>
            </div>
            <p className="text-xs text-muted-foreground text-center mt-2">
              Analytics data will be available once the expert receives interactions
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
